import { isNil } from '~/utilities/type-guards';
import { getLocalStorage, setLocalStorage } from './localStorageHelpers';
import { LocalStorageKey } from './Constants';

export type ContactIdentity = {
  accountHashedId: string;
  lastUsed: number;
  uniqueId: string;
  visitorKey: string;
};

export type ContactIdentityVerificationData = {
  accountHashedId: string;
  contactIdentities: ContactIdentity[];
  embedHashedId?: string;
  embedType?: string;
};

export type ContactIdentityVerification = {
  contactIdentity?: ContactIdentity | null;
  registered?: boolean;
  restricted: boolean;
};

export type ContactIdentityVerificationError = { error: string };

export const upsertContactIdentity = (identity: ContactIdentity): void => {
  const accountIdentities = getLocalStorage(LocalStorageKey.CONTACT_IDENTITIES) as
    | Record<string, ContactIdentity[]>
    | undefined;

  if (isNil(accountIdentities) || isNil(accountIdentities[identity.accountHashedId])) {
    setLocalStorage(LocalStorageKey.CONTACT_IDENTITIES, { [identity.accountHashedId]: [identity] });
    return;
  }

  const existingIdentity = accountIdentities[identity.accountHashedId].find(
    (id: ContactIdentity) => {
      return id.visitorKey === identity.visitorKey && id.uniqueId === identity.uniqueId;
    },
  );

  if (existingIdentity) {
    if (existingIdentity.lastUsed >= identity.lastUsed) {
      return;
    }

    const identities = accountIdentities[identity.accountHashedId].filter(
      (id: ContactIdentity) =>
        !(id.visitorKey === identity.visitorKey && id.uniqueId === identity.uniqueId),
    );
    accountIdentities[identity.accountHashedId] = [identity, ...identities];
  } else {
    accountIdentities[identity.accountHashedId].unshift(identity);
  }

  setLocalStorage(LocalStorageKey.CONTACT_IDENTITIES, accountIdentities);
};

export const getContactIdentities = (accountHashedId?: string, limit = 1): ContactIdentity[] => {
  if (isNil(accountHashedId)) {
    return [];
  }

  const accountIdentities = getLocalStorage(LocalStorageKey.CONTACT_IDENTITIES) as
    | Record<string, ContactIdentity[]>
    | undefined;

  return (
    accountIdentities?.[accountHashedId]
      ?.sort((id1, id2) => id2.lastUsed - id1.lastUsed)
      .slice(0, limit) ?? []
  );
};
